import { Box, Button, Heading, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { InputBuild, ModalBuild, SelectBuild } from 'components/molecules'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { AddressKeys } from 'interfaces/Address'
import { InstitutionsResponseDTO } from 'models'
import { RefObject, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { createAddress, editAddress } from 'services/address'
import { getCEP } from 'services/viacep'
import { useDebounce } from 'utils/useDebounce'
import { AddressForm } from './form'
import { AddressSchemaForm } from './interfaces'
import { schema } from './schema'

interface PropsModalAddress {
    searching: boolean
    itemEdit: any
    refModal: RefObject<ModalBuildHandle>
    handleClose: (callbackGet?: boolean) => void
    setSearching: (search: boolean) => void
    institution: InstitutionsResponseDTO
    setItemEdit: React.Dispatch<React.SetStateAction<any>>
}

export function ModalAddress({
    setSearching,
    searching,
    handleClose,
    institution,
    refModal,
    itemEdit,
    setItemEdit
}: PropsModalAddress) {
    const form = useMemo(() => AddressForm, [AddressForm])
    const toast = useToast()

    useEffect(() => {
        if (itemEdit?.id) {
            Object.keys(itemEdit).forEach((field: any) => {
                setValue(field, itemEdit[field])
            })
        }
    }, [itemEdit])

    const {
        handleSubmit,
        register,
        watch,
        reset,
        formState: { errors, isSubmitting },
        setValue
    } = useForm<AddressSchemaForm>({
        resolver: yupResolver(schema)
    })

    const handleCEP = (cep: string) => {
        const value = cep.replace(/\D/g, '')
        setSearching(value.length === 8)
        fetchConsultCEP(value)
    }

    function emitToastError(error: any, label = 'Registrar') {
        const message =
            error?.response?.data[0]?.mensagem ??
            'Algo deu errado, tente novamente mais tarde.'

        toast({
            title: `Falha ao ${label} Endereço`,
            description: message,
            position: 'bottom-left',
            status: 'error',
            isClosable: true
        })
    }

    function emitToastSuccess(label = 'cadastrado') {
        toast({
            title: `Endereço`,
            description: `O endereço foi ${label} com sucesso!`,
            position: 'bottom-left',
            status: 'success',
            isClosable: true
        })
    }

    async function fetchCreateUser(values: AddressSchemaForm) {
        try {
            await createAddress(institution?.id, values)
            emitToastSuccess()
            handleCancel(true)
        } catch (error: unknown) {
            emitToastError(error)
        }
    }

    async function fetchEditUser(data: AddressSchemaForm) {
        try {
            const payload = {
                idInstitution: institution?.id,
                data,
                idAddress: itemEdit.id
            }
            await editAddress(payload)
            emitToastSuccess('editado')
            handleCancel(true)
        } catch (error: unknown) {
            emitToastError(error, 'Editar')
        }
    }

    const handleFormSubmit = async (data: AddressSchemaForm) => {
        if (!itemEdit?.id) await fetchCreateUser(data)
        else await fetchEditUser(data)
    }

    const handleCancel: any = (isSave = false) => {
        reset()
        handleClose(isSave)
    }

    const hasError = (id: AddressKeys) => {
        return errors && errors[id]
    }

    const handleReset = () => {
        reset()
        setItemEdit({})
    }

    const fetchConsultCEP = useDebounce(async (cep: string) => {
        const isValidCEP = cep.length === 8
        if (!isValidCEP) return

        await getCEP(cep)
            .then(response => {
                const { localidade, logradouro, bairro, uf } = response
                setValue('municipio', localidade)
                setValue('logradouro', logradouro)
                setValue('bairro', bairro)
                setValue('uf', uf)
            })
            .finally(() => {
                setSearching(false)
            })
    }, 300)

    return (
        <ModalBuild
            ref={refModal}
            closeOnOverlayClick={false}
            size="6xl"
            title=" "
            onCancel={() => handleReset()}
        >
            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <Layout type="container" px={8} full display="grid" gap={8}>
                    <Layout type="row">
                        <Box>
                            <Heading
                                display="flex"
                                size="md"
                                alignItems="center"
                                justifyContent={{
                                    base: 'space-between',
                                    sm: 'center'
                                }}
                            >
                                {itemEdit?.cep ? 'Editar' : 'Adicionar'}{' '}
                                Endereço
                            </Heading>
                        </Box>
                    </Layout>
                    <Layout type="row" gutter={{ y: 1.5 }} mb={6}>
                        {form.map(({ layout, input, select }, key) => (
                            <Layout {...layout} key={key}>
                                {select ? (
                                    <SelectBuild
                                        {...select}
                                        id={select.id}
                                        err={hasError(select?.id as any)}
                                        {...register(select.id)}
                                        isSearching={
                                            select.isSearching && searching
                                        }
                                    />
                                ) : (
                                    input && (
                                        <InputBuild
                                            {...input}
                                            id={input.id}
                                            err={hasError(input.id as any)}
                                            {...register(input.id)}
                                            isSearching={
                                                input.isSearching && searching
                                            }
                                            onKeyUp={e =>
                                                input.id === 'cep' &&
                                                handleCEP(
                                                    e.currentTarget
                                                        .value as string
                                                )
                                            }
                                            value={watch(input.id) as string}
                                        />
                                    )
                                )}
                            </Layout>
                        ))}{' '}
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={6}
                            mt={8}
                        >
                            <Button
                                colorScheme="primary"
                                variant="outline"
                                onClick={handleCancel}
                            >
                                Cancelar
                            </Button>
                            <Button
                                colorScheme="primary"
                                type="submit"
                                isLoading={isSubmitting}
                            >
                                Salvar
                            </Button>
                        </Box>
                    </Layout>
                </Layout>
            </form>
        </ModalBuild>
    )
}
